import axios from 'axios';
import { createAsyncThunk } from 'app/store/utils';
import { buildQueryUrl } from 'app/utils/utils';
import {
  FetchGlobalSearchResponse,
  FetchSongItemsResponse,
  Song,
} from 'types/song';
import { addAlertAction } from 'app/store/Alerts/slice';
import { getSongListLinkSelector } from 'app/store/SongList/selectors';
import { ProfileCard } from 'types/profile';
import { EntityType } from 'types/enums';
import { FetchGlobalSearchData } from 'app/store/GlobalSearch/effects.types';
import {
  getGlobalSearchByCollaborator,
  getGlobalSearchLink,
  getSearchValue,
} from './selectors';

export const fetchGlobalSearchEffect = createAsyncThunk(
  'globalSearch/fetchGlobalSearch',
  async (data: FetchGlobalSearchData | undefined, { getState, dispatch }) => {
    const { next, offset, limit, group } = data || {};
    const search = getSearchValue(getState());
    const searchByCollaborator = getGlobalSearchByCollaborator(getState());

    if (!search && !searchByCollaborator) {
      return null;
    }

    const fetchFromGlobalEndpoint = async () => {
      const { searchScope = [EntityType.SONG, EntityType.COLLABORATOR] } =
        data || {};
      const link = getGlobalSearchLink(getState());

      if (!link) {
        throw new Error('No global search link found');
      }

      const url =
        next?.href ||
        buildQueryUrl(link.href, {
          ...(offset ? { offset: offset.toString() } : {}),
          ...(limit ? { limit: limit.toString() } : {}),
          ...(search ? { search } : {}),
          ...(group ? { group: 'true' } : {}),
          searchScope,
        });

      const response = await axios.get<FetchGlobalSearchResponse>(url, {
        withCredentials: true,
      });

      const { items, _links } = response?.data?.response.globalSearchResult;

      const songs: Song[] = [];
      const collaborators: ProfileCard[] = [];

      items?.forEach((item) => {
        if (item.entityType === EntityType.SONG) {
          songs.push(item as Song);
        }
        if (item.entityType === EntityType.COLLABORATOR) {
          collaborators.push(item as ProfileCard);
        }
      });

      return { items: { songs, collaborators }, _links };
    };

    const fetchFromSongsList = async () => {
      const link = getSongListLinkSelector(getState());

      if (!link) {
        throw new Error('No global search link found');
      }

      const url =
        next?.href ||
        buildQueryUrl(link.href, {
          ...(offset ? { offset: offset.toString() } : {}),
          ...(limit ? { limit: limit.toString() } : {}),
          ...(search ? { search } : {}),
          ...(searchByCollaborator
            ? { collaboratingWith: [searchByCollaborator?.subject] }
            : {}),
        });

      const response = await axios.get<FetchSongItemsResponse>(url, {
        withCredentials: true,
      });

      const { items, _links } = response.data.response.song;
      return { items: { songs: items }, _links };
    };

    try {
      let result;
      if (searchByCollaborator) {
        result = await fetchFromSongsList();
      } else {
        result = await fetchFromGlobalEndpoint();
      }

      return result;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);
